import {
  Calendar,
  ExternalLink,
  LayoutDashboard,
  List,
  LogOut,
  Mails,
  Megaphone,
  Menu,
  Radio,
  // Search,
  Settings,
  X
} from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { NotFoundImage } from '../utils/function';
import ConfirmModal from './model/ConfirmModal';

const navItems = [
  { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
  { name: 'Categories', icon: List, path: '/admin/categories' },
  { name: 'Shows', icon: Radio, path: '/admin/shows' },
  { name: 'Schedule', icon: Calendar, path: '/admin/schedule' },
  { name: 'Ads Manager', icon: Megaphone, path: '/admin/ads' },
  { name: 'Newsletters', icon: Mails, path: '/admin/newsletters' },
  { name: 'Settings', icon: Settings, path: '/admin/settings' },
  // { name: 'News', icon: Newspaper, path: '/news' },
  // { name: 'Events', icon: CalendarDays, path: '/events' },
  // { name: 'Funerals', icon: Flower, path: '/funerals' },
  // { name: 'Foundation', icon: Heart, path: '/foundation' },
  // { name: 'Notifications', icon: Bell, path: '/notifications' },
  // { name: 'Team', icon: Users, path: '/team' },
];

// const dummyNotifications = [
//   { id: 1, title: 'New User Registered', message: 'John Doe created an account.', time: '2 min ago', unread: true },
//   { id: 2, title: 'Server Maintenance', message: 'Scheduled for tonight at 3 AM.', time: '1 hour ago', unread: false },
//   { id: 3, title: 'New Ad Request', message: 'Coca Cola requested a banner slot.', time: '3 hours ago', unread: true },
// ];

export default function AdminLayout() {
  const { logout, user, siteData } = useAuth();
  // console.log("user :-",user?.role)

  // State for Desktop Collapse
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // State for Mobile Visibility
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  // State for Notification Dropdown
  // const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const [logoutOpen, setLogoutOpen] = useState(false)

  const location = useLocation();
  const navigate = useNavigate();

  const logoutPopup=()=>{
    setLogoutOpen(true)
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#020b12] flex text-white overflow-hidden bg-[url('https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center bg-no-repeat bg-fixed bg-blend-overlay">
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/90 z-0 pointer-events-none" />

      {/* --- Mobile Sidebar Overlay (Backdrop) --- */}
      {isMobileOpen && (
        <div
          onClick={() => setIsMobileOpen(false)}
          className="fixed inset-0 bg-black/80 z-30 lg:hidden backdrop-blur-sm transition-opacity"
        />
      )}

      {/* --- SIDEBAR --- */}
      <aside
        className={`
          fixed lg:relative top-0 bottom-0 left-0 z-40 h-screen 
          flex flex-col border-r border-white/10 bg-[#042033]/95 lg:bg-[#042033]/50 backdrop-blur-xl transition-all duration-300
          
          /* Mobile: Slide in/out logic */
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 
          
          /* Width Logic: Fixed width on mobile, variable on desktop */
          w-72 ${isSidebarOpen ? 'lg:w-72' : 'lg:w-20'}
        `}
      >
        <div className="h-24 flex items-center px-6 border-b border-white/10 relative">
          {/* Close button for Mobile only */}
          <button
            className="absolute right-4 text-slate-400 hover:text-white lg:hidden"
            onClick={() => setIsMobileOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>

          <div className={`flex items-center gap-3 ${!isSidebarOpen && 'lg:justify-center w-full'}`}>
            {/* Logic: Show full logo if Mobile OR Desktop Expanded */}
            {(isSidebarOpen || isMobileOpen) ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-linear-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/20">
                  <img src={siteData?.headerLogo} alt="Logo" className="w-5 h-8 object-contain drop-shadow-md" />
                </div>
                {/* Hide text only if on Desktop AND Collapsed */}
                <div className={`${(!isSidebarOpen && !isMobileOpen) ? 'hidden' : 'block'}`}>
                  <h1 className="font-bold text-lg tracking-tight leading-none">Akaram</h1>
                  <span className="text-xs text-cyan-400 font-medium tracking-widest uppercase">Admin Panel</span>
                </div>
              </div>
            ) : (
              /* Desktop Collapsed View */
              <div className="w-10 h-10 bg-linear-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/20">
                <img src={siteData?.headerLogo} alt="Logo" className="w-5 h-8 object-contain drop-shadow-md" />
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-2 custom-scrollbar">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileOpen(false)} // Close sidebar on mobile when link clicked
                className={`flex items-center gap-3 px-4 py-3 rounded-xl group relative overflow-hidden ${isActive
                  ? 'bg-linear-to-r from-cyan-600/20 to-blue-600/20 text-white shadow-inner border border-white/5'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
                  } ${!isSidebarOpen && 'lg:justify-center lg:px-2'}`}
              >
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-cyan-500 rounded-full" />}
                <item.icon className={`w-5 h-5 shrink-0 group-hover:scale-110 ${isActive ? 'text-cyan-400' : ''}`} />
                {/* Show text if Mobile OR Desktop Expanded */}
                {(isSidebarOpen || isMobileOpen) && <span className="font-medium">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-cyan-400 hover:bg-cyan-500/10 transition-all duration-200 ${!isSidebarOpen && 'lg:justify-center'}`}
          >
            <ExternalLink className="w-5 h-5 shrink-0" />
            {(isSidebarOpen || isMobileOpen) && <span className="font-medium">Visit Website</span>}
          </a>

          <button
            onClick={logoutPopup}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200 ${!isSidebarOpen && 'lg:justify-center'}`}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {(isSidebarOpen || isMobileOpen) && <span className="font-medium">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* --- MAIN CONTENT --- */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10 h-screen">
        {/* Header */}
        <header className="h-20 border-b border-white/10 bg-[#042033]/30 backdrop-blur-md flex items-center justify-between px-4 sm:px-8 z-50">
          <div className="flex items-center gap-4">
            {/* Desktop Toggle Button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors lg:block hidden"
            >
              {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Mobile Toggle Button */}
            <button
              onClick={() => setIsMobileOpen(true)}
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Search Bar */}
            {/* <div className="hidden md:flex items-center gap-2 bg-black/20 border border-white/10 rounded-full px-4 py-2 w-64 focus-within:w-80 focus-within:bg-black/40 transition-all">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search..."
                className="bg-transparent border-none outline-none text-sm text-white placeholder:text-slate-500 w-full"
              />
            </div> */}
          </div>

          <div className="flex items-center gap-6">
            {/* Notification Bell */}
            {/* <div className="relative">
              <button
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="relative p-2 text-slate-400 hover:text-white transition-colors hover:bg-white/5 rounded-full outline-none"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-[#042033]"></span>
              </button>

              {isNotificationsOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsNotificationsOpen(false)}
                  />
                  <div className="fixed left-4 right-4 top-20 mt-2 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 bg-[#0B1623] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                    <div className="p-4 border-b border-white/10 bg-white/5 flex justify-between items-center">
                      <h3 className="font-bold text-white">Notifications</h3>
                      <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded-full">3 New</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto custom-scrollbar">
                      {dummyNotifications.map(n => (
                        <div key={n.id} className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${n.unread ? 'bg-cyan-500/5' : ''}`}>
                          <div className="flex justify-between items-start mb-1">
                            <p className={`text-sm font-medium ${n.unread ? 'text-white' : 'text-slate-400'}`}>{n.title}</p>
                            <span className="text-[10px] text-slate-500">{n.time}</span>
                          </div>
                          <p className="text-xs text-slate-400 leading-relaxed">{n.message}</p>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 text-center border-t border-white/10 bg-white/5">
                      <Link
                        to="/notifications"
                        className="text-xs text-cyan-400 hover:text-cyan-300 font-medium hover:underline"
                        onClick={() => setIsNotificationsOpen(false)}
                      >
                        View All Notifications
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div> */}

            <Link to="/admin/profile" className="flex items-center gap-3 pl-6 border-l border-white/10 hover:opacity-80 transition-opacity">
              <div className="text-right hidden sm:block">
                <p className="text-xs text-cyan-400">{user?.email}</p>
                <p className="text-sm font-medium text-white">{user?.name}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-linear-to-br from-cyan-400 to-blue-500 p-[2px]">
                <div className="w-full h-full rounded-full bg-black overflow-hidden">
                  <img
                    src={user?.profile ?? NotFoundImage}
                    alt={user?.name}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = NotFoundImage;
                    }}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              </div>
            </Link>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-8 custom-scrollbar">
          <div className="max-w-7xl mx-auto">
            <ConfirmModal
              isOpen={logoutOpen}
              onClose={() => setLogoutOpen(false)}
              onConfirm={handleLogout}
              title="Logout"
              message="Are you sure you want to logout? You will need to login again to access your account."
              confirmText="Logout"
              cancelText="Cancel"
              isDestructive={true}
            />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}